import { useTranslation } from "react-i18next";

export type RuleType = "fixed_monthly" | "hourly" | "per_lesson" | "percentage";
export type ProgSal = { rule_type: RuleType; amount: string; teacher_share: string; company_share: string; currency: string };

export const emptyProgSal = (currency: string): ProgSal => ({ rule_type: "per_lesson", amount: "", teacher_share: "60", company_share: "40", currency });

// Returns an i18n error key or the API rule payload.
export function buildProgramRule(programId: string, s: ProgSal): { error: string } | { rule: any } {
    if (s.rule_type === "percentage") {
        const ts = parseFloat(s.teacher_share), cs = parseFloat(s.company_share);
        if (isNaN(ts) || isNaN(cs) || ts < 0 || cs < 0 || ts > 100 || cs > 100) return { error: "staff.invite_err.invalid_salary_amount" };
        if (Math.abs(ts + cs - 100) > 0.01) return { error: "staff.invite_err.percent_sum_100" };
        return { rule: { program_id: programId, rule_type: "percentage", teacher_share_percent: ts, company_share_percent: cs, currency: s.currency } };
    }
    const n = parseFloat(s.amount);
    if (!s.amount || isNaN(n) || n < 0) return { error: "staff.invite_err.invalid_salary_amount" };
    return { rule: { program_id: programId, rule_type: s.rule_type, amount_minor: Math.round(n * 100), currency: s.currency } };
}

const num = (v: string) => v.replace(/[^0-9.,]/g, "").replace(",", ".");
const other = (v: string, fallback: string) => { const n = parseFloat(v); return isNaN(n) ? fallback : String(Math.max(0, Math.round((100 - n) * 100) / 100)); };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>{children}</label>;
}

function ProgramSalaryRow({ program, value, onChange }: { program: { id: string; name: string }; value: ProgSal; onChange: (v: ProgSal) => void }) {
    const { t } = useTranslation();
    const cls = "surface-input px-2 py-1 w-full text-sm";
    const st = { minHeight: 36 };
    const id = program.id;
    return (
        <div className="rounded-md border p-2 space-y-2" style={{ borderColor: "var(--border)", background: "var(--input)" }} data-testid={`invite-sal-prog-${id}`}>
            <div className="text-xs font-medium" style={{ color: "var(--text)" }} data-testid={`invite-sal-prog-name-${id}`}>{program.name}</div>
            <div className="grid grid-cols-4 gap-2">
                <Field label={t("staff.invite.salary_model")}>
                    <select data-testid={`invite-sal-prog-model-${id}`} value={value.rule_type} onChange={(e) => onChange({ ...value, rule_type: e.target.value as RuleType })} className={cls} style={st}>
                        <option value="fixed_monthly">{t("staff.invite.salary_fixed")}</option>
                        <option value="hourly">{t("staff.invite.salary_hourly")}</option>
                        <option value="per_lesson">{t("staff.invite.salary_per_lesson")}</option>
                        <option value="percentage">{t("staff.invite.salary_percentage")}</option>
                    </select>
                </Field>
                {value.rule_type === "percentage" ? (
                    <>
                        <Field label={t("staff.invite.teacher_share")}>
                            <input data-testid={`invite-sal-prog-teacher-share-${id}`} inputMode="decimal" value={value.teacher_share} onChange={(e) => { const v = num(e.target.value); onChange({ ...value, teacher_share: v, company_share: other(v, value.company_share) }); }} className={cls} style={st} />
                        </Field>
                        <Field label={t("staff.invite.company_share")}>
                            <input data-testid={`invite-sal-prog-company-share-${id}`} inputMode="decimal" value={value.company_share} onChange={(e) => { const v = num(e.target.value); onChange({ ...value, company_share: v, teacher_share: other(v, value.teacher_share) }); }} className={cls} style={st} />
                        </Field>
                    </>
                ) : (
                    <div className="col-span-2">
                        <Field label={t("staff.invite.salary_amount")}>
                            <input data-testid={`invite-sal-prog-amount-${id}`} inputMode="decimal" placeholder="0.00" value={value.amount} onChange={(e) => onChange({ ...value, amount: num(e.target.value) })} className={cls} style={st} />
                        </Field>
                    </div>
                )}
                <Field label={t("staff.invite.salary_currency")}>
                    <input data-testid={`invite-sal-prog-currency-${id}`} maxLength={3} value={value.currency} onChange={(e) => onChange({ ...value, currency: e.target.value.toUpperCase() })} className={`${cls} uppercase`} style={st} />
                </Field>
            </div>
        </div>
    );
}

export function ProgramSalaryRows({ programs, values, onChange, currency }: {
    programs: { id: string; name: string }[]; values: Record<string, ProgSal>;
    onChange: (id: string, v: ProgSal) => void; currency: string;
}) {
    const { t } = useTranslation();
    return (
        <div className="space-y-2" data-testid="invite-sal-prog-list">
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("staff.invite.salary_per_program")}</div>
            {programs.map((p) => (
                <ProgramSalaryRow key={p.id} program={p} value={values[p.id] || emptyProgSal(currency)} onChange={(v) => onChange(p.id, v)} />
            ))}
        </div>
    );
}
